/**
 * iOS Safari yardımcı fonksiyonları
 * iOS cihazlarda medya oynatma ve kamera erişimi için özel düzeltmeler
 */

// iOS cihaz tespiti
export const isIOSDevice = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

// iOS Safari tespiti
export const isIOSSafari = () => {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|mercury/.test(ua);
};

// Android cihaz tespiti
export const isAndroidDevice = () => {
  return /Android/.test(navigator.userAgent);
};

// Mobil cihaz tespiti
export const isMobileDevice = () => {
  return isIOSDevice() || isAndroidDevice() || /Mobile|Android|iPhone|iPad|iPod/.test(navigator.userAgent);
};

// HTTPS kontrolü
export const isHTTPS = () => {
  return window.location.protocol === 'https:' || window.location.hostname === 'localhost';
};

// Local storage'da kamera iznini sakla
export const saveCameraPermission = (status) => {
  try {
    localStorage.setItem('cameraPermission', status);
    localStorage.setItem('cameraPermissionTimestamp', Date.now().toString());
  } catch (e) {
    console.error('Kamera izni kaydedilemedi:', e);
  }
};

// Local storage'dan kamera iznini al
export const getSavedCameraPermission = () => {
  try {
    const status = localStorage.getItem('cameraPermission');
    const timestamp = localStorage.getItem('cameraPermissionTimestamp');
    
    // İzin 24 saatten eski ise yeniden kontrol et
    if (status && timestamp) {
      const isExpired = Date.now() - parseInt(timestamp) > 24 * 60 * 60 * 1000;
      if (!isExpired) {
        return status;
      }
    }
  } catch (e) {
    console.error('Kaydedilmiş kamera izni okunamadı:', e);
  }
  return null;
};

// Kamera izni durumu kontrolü - Yeni fonksiyon
export const checkCameraPermissionStatus = async () => {
  if (!isMobileDevice()) return 'granted';

  // Önce local storage'dan kontrol et
  const savedPermission = getSavedCameraPermission();
  if (savedPermission) {
    return savedPermission;
  }

  try {
    // Permission API varsa kullan
    if (navigator.permissions) {
      const permission = await navigator.permissions.query({ name: 'camera' });
      console.log('Kamera izni durumu:', permission.state);
      saveCameraPermission(permission.state);
      return permission.state;
    }

    // Permission API yoksa getUserMedia ile test et
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    stream.getTracks().forEach(track => track.stop());
    saveCameraPermission('granted');
    return 'granted';

  } catch (error) {
    console.log('Kamera izni kontrolü hatası:', error.name);
    
    if (error.name === 'NotAllowedError') {
      return 'denied';
    } else if (error.name === 'NotFoundError') {
      return 'not-found';
    } else if (error.name === 'NotSupportedError') {
      return 'not-supported';
    } else {
      return 'unknown';
    }
  }
};

// Kamera izni isteme - Yeni fonksiyon
export const requestCameraPermission = async () => {
  if (!isMobileDevice()) return true;

  try {
    console.log('Kamera izni isteniyor...');

    // iOS için özel kontroller
    if (isIOSDevice()) {
      if (!isHTTPS()) {
        throw new Error('iOS cihazlarda kamera erişimi için HTTPS bağlantısı gereklidir');
      }
    }

    // Kamera izni iste
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'environment', // Arka kamera
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 }
      }
    });

    // Test başarılıysa stream'i kapat
    stream.getTracks().forEach(track => {
      console.log('Kamera track bilgisi:', track.getSettings());
      track.stop();
    });

    console.log('Kamera izni verildi');
    return true;

  } catch (error) {
    console.error('Kamera izni hatası:', error);
    throw error;
  }
};

// Mobil cihazlar için geliştirilmiş kamera izni kontrolü
export const checkCameraPermissionIOS = async () => {
  if (!isMobileDevice()) return true;

  try {
    // Önce mevcut izin durumunu kontrol et
    const permissionStatus = await checkCameraPermissionStatus();
    
    if (permissionStatus === 'granted') {
      console.log('Kamera izni zaten verilmiş');
      return true;
    }
    
    if (permissionStatus === 'denied') {
      throw new Error('Kamera erişimi reddedildi. Tarayıcı ayarlarından izni kontrol edin.');
    }

    // İzin durumu 'prompt' veya 'unknown' ise izin iste
    if (permissionStatus === 'prompt' || permissionStatus === 'unknown') {
      return await requestCameraPermission();
    }

    throw new Error('Kamera erişimi mevcut değil');

  } catch (error) {
    console.error('Kamera izni kontrolü hatası:', error);

    // Daha açıklayıcı hata mesajları
    if (error.name === 'NotAllowedError') {
      if (isIOSDevice()) {
        throw new Error('Kamera erişimi reddedildi. Safari ayarlarından kamera iznini kontrol edin ve sayfayı yenileyin.');
      } else {
        throw new Error('Kamera erişimi reddedildi. Tarayıcı ayarlarından kamera iznini kontrol edin.');
      }
    } else if (error.name === 'NotFoundError') {
      throw new Error('Kamera bulunamadı. Cihazınızda kamera olduğundan emin olun.');
    } else if (error.name === 'NotSupportedError') {
      throw new Error('Kamera bu tarayıcıda desteklenmiyor. Safari veya Chrome kullanın.');
    } else if (error.name === 'NotReadableError') {
      throw new Error('Kamera başka bir uygulama tarafından kullanılıyor. Diğer uygulamaları kapatın.');
    } else if (error.name === 'OverconstrainedError') {
      throw new Error('Kamera ayarları desteklenmiyor. Farklı bir kamera deneyin.');
    }

    throw error;
  }
};

// iOS için geliştirilmiş medya oynatma hazırlığı
export const prepareAudioForIOS = (audioElement) => {
  if (!isIOSDevice() || !audioElement) return;

  try {
    console.log('iOS audio element hazırlanıyor...');

    // iOS Safari için temel ayarlar
    audioElement.preload = 'metadata';
    audioElement.playsInline = true;
    audioElement.muted = false;
    audioElement.volume = 1.0;
    audioElement.controls = false;
    audioElement.autoplay = false;

    // CORS ayarları
    audioElement.crossOrigin = 'anonymous';
    audioElement.setAttribute('crossorigin', 'anonymous');

    // iOS Safari için webkit özellikleri
    audioElement.setAttribute('webkit-playsinline', 'true');
    audioElement.setAttribute('playsinline', 'true');

    // iOS Safari için ek performans ayarları
    audioElement.style.webkitAppearance = 'none';
    audioElement.style.outline = 'none';
    audioElement.style.border = 'none';
    audioElement.style.background = 'transparent';

    // iOS Safari için medya session ayarları
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = null;
    }

    // iOS Safari için ses formatı kontrolü
    const src = audioElement.src || audioElement.getAttribute('src');
    if (src) {
      const formatCheck = checkIOSMediaSupport(src);
      if (!formatCheck.supported) {
        console.warn('iOS: Desteklenmeyen ses formatı:', formatCheck.format);
      }
    }

    console.log('iOS audio hazırlığı tamamlandı');
  } catch (error) {
    console.error('iOS audio hazırlık hatası:', error);
  }
};

// iOS için video hazırlığı - Geliştirilmiş
export const prepareVideoForIOS = (videoElement) => {
  if (!isIOSDevice() || !videoElement) return;

  try {
    console.log('iOS video element hazırlanıyor...');

    // iOS için özel ayarlar
    videoElement.playsInline = true;
    videoElement.muted = true;
    videoElement.autoplay = true;
    videoElement.controls = false;
    videoElement.preload = 'metadata';
    
    // iOS için webkit özellikleri
    videoElement.setAttribute('webkit-playsinline', 'true');
    videoElement.setAttribute('playsinline', 'true');
    videoElement.setAttribute('webkit-playsinline', 'true');
    
    // iOS için ek performans ayarları
    videoElement.style.webkitAppearance = 'none';
    videoElement.style.outline = 'none';
    videoElement.style.border = 'none';
    videoElement.style.background = 'transparent';
    
    console.log('iOS video hazırlığı tamamlandı');
  } catch (error) {
    console.error('iOS video hazırlık hatası:', error);
  }
};

// Mobil cihazlar için kamera stream başlatma - Geliştirilmiş
export const startCameraStream = async (videoElement, options = {}) => {
  if (!isMobileDevice() || !videoElement) {
    throw new Error('Geçersiz parametreler');
  }

  try {
    console.log('Kamera stream başlatılıyor...');

    // Önce kamera iznini kontrol et
    await checkCameraPermissionIOS();

    // Varsayılan ayarlar - arka kamera odaklı
    const defaultConstraints = {
      video: {
        facingMode: 'environment', // Arka kamera
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 },
        aspectRatio: { ideal: 16/9 },
        frameRate: { ideal: 30, max: 60 }
      }
    };

    // iOS için optimize edilmiş ayarlar
    if (isIOSDevice()) {
      defaultConstraints.video = {
        facingMode: 'environment',
        width: { ideal: 640, max: 1280 },
        height: { ideal: 480, max: 720 },
        frameRate: { ideal: 24, max: 30 }
      };
    }

    // Kullanıcı ayarlarını birleştir
    const constraints = {
      video: {
        ...defaultConstraints.video,
        ...options.video
      }
    };

    console.log('Kamera constraints:', constraints);

    // Stream başlat
    const stream = await navigator.mediaDevices.getUserMedia(constraints);

    // Video element'e stream'i bağla
    videoElement.srcObject = stream;
    
    // iOS için video hazırlığı
    if (isIOSDevice()) {
      prepareVideoForIOS(videoElement);
    }

    // Stream'in hazır olmasını bekle
    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Video stream hazırlanma zaman aşımı'));
      }, 10000);

      const onLoadedMetadata = () => {
        clearTimeout(timeout);
        videoElement.removeEventListener('loadedmetadata', onLoadedMetadata);
        videoElement.removeEventListener('error', onError);
        resolve();
      };

      const onError = (e) => {
        clearTimeout(timeout);
        videoElement.removeEventListener('loadedmetadata', onLoadedMetadata);
        videoElement.removeEventListener('error', onError);
        reject(new Error('Video stream hatası: ' + e.message));
      };

      videoElement.addEventListener('loadedmetadata', onLoadedMetadata);
      videoElement.addEventListener('error', onError);
    });

    console.log('Kamera stream başarıyla başlatıldı');
    return stream;

  } catch (error) {
    console.error('Kamera stream başlatma hatası:', error);
    throw error;
  }
};

// iOS için geliştirilmiş ses oynatma deneme
export const tryPlayAudioIOS = async (audioElement) => {
  if (!isIOSDevice() || !audioElement) return false;

  try {
    console.log('iOS ses oynatma başlatılıyor...');

    // iOS için audio element'i yeniden hazırla
    prepareAudioForIOS(audioElement);

    // iPhone 11 için özel ayarlar
    audioElement.playsInline = true;
    audioElement.muted = false;
    audioElement.volume = 1;
    audioElement.preload = 'metadata';
    audioElement.crossOrigin = 'anonymous';

    // Webkit özellikleri
    audioElement.setAttribute('webkit-playsinline', 'true');
    audioElement.setAttribute('playsinline', 'true');
    audioElement.setAttribute('crossorigin', 'anonymous');

    // iOS Range Request desteği için özel ayarlar
    audioElement.setAttribute('preload', 'metadata');
    
    // iOS'ta bazen currentTime'ı ayarlamak gerekebilir
    if (audioElement.currentTime === 0) {
      audioElement.currentTime = 0.01; // Çok küçük bir değer
    }

    // Ses dosyasını yeniden yükle
    audioElement.load();

    // iOS Safari için kısa bekleme
    await new Promise(resolve => setTimeout(resolve, 300));

    // Ses dosyasının yüklenmesini bekle - iOS için daha uzun timeout
    if (audioElement.readyState < 2) {
      console.log('iOS: Ses dosyası yükleniyor...');

      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          cleanup();
          reject(new Error('iOS ses yükleme zaman aşımı (12 saniye)'));
        }, 12000); // iPhone 11 için daha uzun timeout

        const onCanPlay = () => {
          console.log('iOS: Ses dosyası yüklendi (canplay)');
          cleanup();
          resolve();
        };

        const onCanPlayThrough = () => {
          console.log('iOS: Ses dosyası tamamen yüklendi (canplaythrough)');
          cleanup();
          resolve();
        };

        const onLoadedData = () => {
          console.log('iOS: Ses dosyası verisi yüklendi (loadeddata)');
          cleanup();
          resolve();
        };

        const onError = (e) => {
          console.error('iOS: Ses yükleme hatası:', e);
          cleanup();
          reject(new Error('iOS ses yükleme hatası: ' + (e.message || 'Bilinmeyen hata')));
        };

        const cleanup = () => {
          clearTimeout(timeout);
          audioElement.removeEventListener('canplay', onCanPlay);
          audioElement.removeEventListener('canplaythrough', onCanPlayThrough);
          audioElement.removeEventListener('loadeddata', onLoadedData);
          audioElement.removeEventListener('error', onError);
        };

        audioElement.addEventListener('canplay', onCanPlay);
        audioElement.addEventListener('canplaythrough', onCanPlayThrough);
        audioElement.addEventListener('loadeddata', onLoadedData);
        audioElement.addEventListener('error', onError);
      });
    }

    console.log('iOS: Ses oynatma başlatılıyor...');

    // iOS için ses çalmayı dene - birden fazla deneme
    let playAttempts = 0;
    const maxAttempts = 5; // iPhone 11 için daha fazla deneme

    while (playAttempts < maxAttempts) {
      try {
        console.log(`iOS: Oynatma denemesi ${playAttempts + 1}/${maxAttempts}`);

        // Her denemede audio element'i yeniden hazırla
        if (playAttempts > 0) {
          prepareAudioForIOS(audioElement);
          audioElement.load();
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        // iOS Range Request desteği için özel oynatma
        const playPromise = audioElement.play();
        if (playPromise !== undefined) {
          await playPromise;
        }

        // Oynatma başladıktan sonra kısa bir bekleme
        await new Promise(resolve => setTimeout(resolve, 200));

        // Oynatmanın gerçekten başladığını kontrol et
        if (!audioElement.paused && audioElement.currentTime > 0) {
          console.log('iOS: Ses başarıyla oynatılıyor');
          return true;
        } else {
          throw new Error('Ses oynatma başlamadı');
        }

      } catch (playError) {
        playAttempts++;
        console.warn(`iOS: Oynatma denemesi ${playAttempts} başarısız:`, playError);

        if (playAttempts < maxAttempts) {
          // Daha uzun bekleme süresi
          await new Promise(resolve => setTimeout(resolve, 800));
        } else {
          throw playError;
        }
      }
    }

    return false;

  } catch (error) {
    console.error('iOS ses oynatma hatası:', error);
    throw error;
  }
};

// iOS hata mesajları - Geliştirilmiş
export const getIOSErrorMessage = (error) => {
  if (!isMobileDevice()) return error.message;

  switch (error.name) {
    case 'NotAllowedError':
      if (isIOSDevice()) {
        return 'iOS: Kamera erişimi reddedildi. Safari ayarlarından kamera iznini kontrol edin ve sayfayı yenileyin.';
      } else {
        return 'Android: Kamera erişimi reddedildi. Tarayıcı ayarlarından kamera iznini kontrol edin.';
      }
    case 'NotFoundError':
      return 'Kamera bulunamadı. Cihazınızda kamera olduğundan emin olun.';
    case 'NotSupportedError':
      return 'Kamera bu tarayıcıda desteklenmiyor. Safari veya Chrome kullanın.';
    case 'NotReadableError':
      return 'Kamera başka bir uygulama tarafından kullanılıyor. Diğer uygulamaları kapatın.';
    case 'AbortError':
      return 'Kamera işlemi iptal edildi. Tekrar deneyin.';
    case 'OverconstrainedError':
      return 'Kamera ayarları desteklenmiyor. Farklı bir kamera deneyin.';
    default:
      if (error.message.includes('zaman aşımı')) {
        return 'Kamera başlatma zaman aşımı. İnternet bağlantınızı kontrol edin ve tekrar deneyin.';
      }
      if (error.message.includes('HTTPS')) {
        return 'iOS cihazlarda kamera erişimi için HTTPS bağlantısı gereklidir.';
      }
      if (error.message.includes('play')) {
        return 'Oynatma hatası. Play butonuna tekrar basın.';
      }
      return `${isIOSDevice() ? 'iOS: ' : ''}${error.message || 'Bilinmeyen kamera hatası. Sayfayı yenileyin.'}`;
  }
};

// iOS için kullanıcı etkileşimi kontrolü
export const requiresUserInteraction = () => {
  return isIOSDevice();
};

// iOS için desteklenen medya formatları kontrolü
export const checkIOSMediaSupport = (url) => {
  if (!isIOSDevice() || !url) return { supported: true, format: 'unknown' };

  const extension = url.split('.').pop()?.toLowerCase();
  const supportedFormats = {
    // Audio formatları
    'mp3': { supported: true, type: 'audio' },
    'aac': { supported: true, type: 'audio' },
    'm4a': { supported: true, type: 'audio' },
    'wav': { supported: true, type: 'audio' },
    'ogg': { supported: false, type: 'audio' }, // iOS desteklemez
    'webm': { supported: false, type: 'audio' }, // iOS desteklemez

    // Video formatları
    'mp4': { supported: true, type: 'video' },
    'm4v': { supported: true, type: 'video' },
    'mov': { supported: true, type: 'video' },
    'webm': { supported: false, type: 'video' }, // iOS desteklemez
    'ogv': { supported: false, type: 'video' } // iOS desteklemez
  };

  const formatInfo = supportedFormats[extension] || { supported: true, type: 'unknown' };

  return {
    supported: formatInfo.supported,
    format: extension,
    type: formatInfo.type,
    recommendation: !formatInfo.supported ?
      `${extension} formatı iOS'ta desteklenmez. MP3 veya MP4 kullanın.` : null
  };
};

// iOS için medya politikası bilgisi
export const getIOSMediaPolicy = () => {
  if (!isMobileDevice()) return null;

  return {
    requiresUserInteraction: isIOSDevice(),
    requiresHTTPS: isIOSDevice(),
    autoplayRestricted: isIOSDevice(),
    volumeControlLimited: isIOSDevice(),
    cameraRequiresPermission: true,
    supportedAudioFormats: ['mp3', 'aac', 'm4a', 'wav'],
    supportedVideoFormats: ['mp4', 'm4v', 'mov'],
    recommendations: [
      'Medya oynatmak için kullanıcı etkileşimi gereklidir',
      'Kamera erişimi için HTTPS bağlantısı gereklidir',
      'Otomatik oynatma kısıtlıdır',
      'Ses seviyesi kontrolü sınırlıdır',
      'İlk oynatmadan önce play butonuna basılmalıdır',
      'MP3, AAC, M4A ses formatları önerilir',
      'MP4, M4V, MOV video formatları önerilir'
    ]
  };
};

// Mobil cihazlar için QR Scanner optimizasyonları - Geliştirilmiş
export const getMobileQRScannerOptions = () => {
  const options = {
    highlightScanRegion: true,
    highlightCodeOutline: true,
    preferredCamera: 'environment', // Arka kamera
    maxScansPerSecond: 3,
    returnDetailedScanResult: true
  };

  // iOS için özel ayarlar
  if (isIOSDevice()) {
    options.maxScansPerSecond = 2; // iOS için daha düşük tarama hızı
    options.calculateScanRegion = (video) => {
      // iOS için özel tarama bölgesi - daha büyük alan
      const smallerSize = Math.min(video.videoWidth, video.videoHeight) * 0.7;
      const x = (video.videoWidth - smallerSize) / 2;
      const y = (video.videoHeight - smallerSize) / 2;
      return {
        x: Math.round(x),
        y: Math.round(y),
        width: Math.round(smallerSize),
        height: Math.round(smallerSize),
      };
    };
  }

  return options;
};
